import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Download, FileText, ArrowRight, Sun, Moon } from "lucide-react";
import { MarkBtb } from "@/components/BrandLogos";
import { API } from "@/lib/api";
import { useDocumentMeta } from "@/lib/meta";

const EVERYONE = [
  "Personalised homepage & 'today' panels — start the day knowing what needs doing.",
  "Smart email filing to the right matter + deadlines caught and diarised.",
  "Time recorded straight from email — billed in the moment, not reconstructed later.",
  "Matter & full-text search across the whole firm.",
  "An in-app assistant sourced from your own SOPs and live matter data.",
];

const BOARD = [
  "Director dashboards across operations, finance and workload.",
  "Live matter economics — quote vs work-in-progress vs money on account.",
  "Filing-compliance & weekly firm-performance reporting.",
  "Firm-wide risk register rolled up from every matter.",
  "Evidence packs and regulatory exports on demand.",
];

const MENU = [
  {
    h: "New-matter pipeline",
    d: "Conflict check → matter reference → automatic file structure → onboarding → SRA-weighted risk assessment → client care letters → signed welcome pack. Every file opened the same compliant way, in minutes.",
  },
  {
    h: "Outlook add-in & webmail",
    d: "An Outlook add-in for filing, deadlines and time-recording from email, plus Outlook-grade webmail — with even more features, so email lives against the right matter, not lost in an inbox.",
  },
  {
    h: "ComplianceMinder",
    d: "Live versioned policies with reasons & approvals, staff attestations, scheduled control tests, breach logging, the MLCO→COLP review chain, firm-wide risk, and downloadable evidence packs.",
  },
  {
    h: "Conveyancing & completions",
    d: "Completions book, completion statements, pre-exchange & pre-completion checklists, and client-completed SDLT and TA6 / TA7 / TA10 forms.",
  },
  {
    h: "Money & oversight",
    d: "Live matter money bar, credit control, trust reporting and director dashboards — the firm's funding position at a glance.",
  },
  {
    h: "Documents & bundles",
    d: "System-wide matter search, full-text search, inline previews and a court-ready bundle builder.",
  },
];

const THEMES = {
  light: {
    page: "#eceae6", card: "#faf9f7", heading: "#14110f", body: "#4a453f", bodyStrong: "#2b2622",
    muted: "#6b6560", hairline: "#e2ded8", accent: "#752023", band: "#14110f", bandEyebrow: "#e0a3a5",
    bandBody: "#ffffff", cta: "#752023", logo: "#14110f",
  },
  dark: {
    page: "#0a0908", card: "#14110f", heading: "#f7f5f2", body: "#cfc9c2", bodyStrong: "#e8e4de",
    muted: "#8a847c", hairline: "#2e2a26", accent: "#d98f91", band: "#f7f5f2", bandEyebrow: "#752023",
    bandBody: "#14110f", cta: "#a33b3e", logo: "#f7f5f2",
  },
};

export default function TheWhy() {
  useDocumentMeta({
    title: "The Why — your roadmap, not a vendor's",
    description:
      "CaseMinder & ComplianceMinder are built around how your firm actually works — down to the fee earner and the board. Your roadmap is your firm's need, not a vendor's release schedule.",
    path: "/the-why",
  });

  const [dark, setDark] = useState(
    () => new URLSearchParams(window.location.search).get("theme") === "dark"
  );
  const t = dark ? THEMES.dark : THEMES.light;
  const eyebrow = "text-[0.62rem] font-bold uppercase tracking-[0.16em]";
  const archivo = { fontFamily: "'Archivo', sans-serif" };

  return (
    <div className="min-h-screen w-full py-10" style={{ background: t.page }} data-testid="the-why-page">
      {/* Action bar */}
      <div className="no-print mx-auto mb-8 flex w-full max-w-[900px] flex-wrap items-center justify-between gap-4 px-6">
        <Link to="/" data-testid="the-why-home" className="text-xs font-semibold uppercase tracking-[0.14em]" style={{ color: t.muted }}>
          ← Beyond the Box
        </Link>
        <div className="flex items-center gap-3">
          <div className="flex items-center border" style={{ borderColor: dark ? "#3a352f" : "#c9c4bc" }}>
            <button type="button" data-testid="the-why-theme-light" onClick={() => setDark(false)}
              className="inline-flex items-center gap-1.5 px-3 py-2.5 text-[0.68rem] font-semibold uppercase tracking-[0.12em] transition-colors"
              style={{ background: !dark ? "#14110f" : "transparent", color: !dark ? "#fff" : t.muted }}>
              <Sun strokeWidth={2} className="h-3.5 w-3.5" /> Light
            </button>
            <button type="button" data-testid="the-why-theme-dark" onClick={() => setDark(true)}
              className="inline-flex items-center gap-1.5 px-3 py-2.5 text-[0.68rem] font-semibold uppercase tracking-[0.12em] transition-colors"
              style={{ background: dark ? "#f7f5f2" : "transparent", color: dark ? "#14110f" : t.muted }}>
              <Moon strokeWidth={2} className="h-3.5 w-3.5" /> Dark
            </button>
          </div>
          <a href={`${API}/the-why/${dark ? "dark" : "light"}.png`} data-testid="the-why-download-png"
            className="inline-flex items-center gap-2 px-5 py-3 text-xs font-semibold uppercase tracking-[0.12em] text-white transition-opacity hover:opacity-90"
            style={{ background: "#752023" }}>
            <Download strokeWidth={2} className="h-4 w-4" /> Download image
          </a>
          <a href={`${API}/the-why/${dark ? "dark" : "light"}.pdf`} data-testid="the-why-download-pdf"
            className="inline-flex items-center gap-2 border px-5 py-3 text-xs font-semibold uppercase tracking-[0.12em] transition-colors"
            style={{ borderColor: dark ? "#3a352f" : "#c9c4bc", color: t.heading }}>
            <FileText strokeWidth={2} className="h-4 w-4" /> Download PDF
          </a>
        </div>
      </div>

      {/* Card */}
      <div id="the-why-card" data-testid="the-why-card" className="mx-auto w-full max-w-[900px] px-10 py-11"
        style={{ background: t.card, fontFamily: "'IBM Plex Sans', sans-serif", color: t.heading }}>

        {/* Masthead */}
        <div className="flex items-center justify-between border-b pb-6" style={{ borderColor: t.hairline }}>
          <div className="flex items-center gap-3" style={{ color: t.logo }}>
            <MarkBtb className="h-9 w-9" strokeWidth={2.25} />
            <span className="text-[0.72rem] font-bold uppercase leading-[1.02] tracking-[0.06em]" style={archivo}>
              <span className="block">Beyond</span><span className="block">the</span><span className="block">Box</span>
            </span>
          </div>
          <span className={eyebrow} style={{ color: t.accent }}>For owner-managed law firms</span>
        </div>

        {/* Headline */}
        <div className="pt-7">
          <h1 className="text-[2.4rem] font-extrabold leading-[0.96] tracking-[-0.03em]" style={{ ...archivo, color: t.heading }}>
            Your roadmap. Not a vendor's.
          </h1>
          <p className="mt-4 max-w-[48rem] text-[15px] leading-relaxed" style={{ color: t.body }}>
            Most legal software makes your firm adapt to the product — and parks what you actually need
            "on the roadmap." Beyond the Box works the other way round. CaseMinder and ComplianceMinder
            are built around how your firm really works — down to the individual fee earner and the board —
            on the Microsoft 365 you already own.
          </p>
        </div>

        {/* Differentiator band */}
        <div className="mt-6 p-5" style={{ background: t.band }}>
          <span className={eyebrow} style={{ color: t.bandEyebrow }}>No "it's on the roadmap"</span>
          <p className="mt-2.5 text-[15px] leading-snug" style={{ color: t.bandBody }}>
            A team member flags a need. We discover it, build it, test it — and it's in their daily
            experience, ridiculously quickly. The roadmap is your firm's immediate need, not a vendor's
            release schedule. You own the system and the data.
          </p>
        </div>

        {/* Tailored to everyone */}
        <div className="mt-7 grid grid-cols-1 gap-8 border-t pt-7 md:grid-cols-2" style={{ borderColor: t.hairline }}>
          <div>
            <span className={eyebrow} style={{ color: t.accent }}>For every fee earner</span>
            <ul className="mt-3 space-y-2" data-testid="the-why-everyone">
              {EVERYONE.map((x, i) => (
                <li key={i} className="flex items-start gap-2.5 text-[13.5px] leading-snug" style={{ color: t.bodyStrong }}>
                  <span style={{ color: t.accent }}>—</span>{x}
                </li>
              ))}
            </ul>
          </div>
          <div className="md:border-l md:pl-8" style={{ borderColor: t.hairline }}>
            <span className={eyebrow} style={{ color: t.accent }}>For the board</span>
            <ul className="mt-3 space-y-2" data-testid="the-why-board">
              {BOARD.map((x, i) => (
                <li key={i} className="flex items-start gap-2.5 text-[13.5px] leading-snug" style={{ color: t.bodyStrong }}>
                  <span style={{ color: t.accent }}>—</span>{x}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* M365 line — end of page 1 */}
        <div className="mt-7 border-t pt-5" style={{ borderColor: t.hairline }}>
          <span className={eyebrow} style={{ color: t.accent }}>Send this to your IT team</span>
          <p className="mt-2.5 text-[13.5px] leading-relaxed" style={{ color: t.body }}>
            Runs in <strong style={{ color: t.heading }}>your own Microsoft 365 tenant</strong> · your data never
            leaves your estate · every Microsoft Graph permission explained · auditable by your own IT team.
          </p>
        </div>

        {/* Page 2 — capability menu */}
        <div style={{ breakBefore: "page", pageBreakBefore: "always" }} className="border-t pt-2" data-testid="the-why-menu">
          <span className={eyebrow} style={{ color: t.accent }}>A working platform, not a promise</span>
          <h2 className="mt-2 text-[1.6rem] font-bold tracking-[-0.02em]" style={{ ...archivo, color: t.heading }}>
            What's already live.
          </h2>
          <div className="mt-5 grid grid-cols-1 gap-x-10 gap-y-4 md:grid-cols-2">
            {MENU.map((m, i) => (
              <div key={i} className="border-t pt-3" style={{ borderColor: t.hairline }}>
                <h3 className="text-[15px] font-bold tracking-[-0.01em]" style={{ ...archivo, color: t.heading }}>{m.h}</h3>
                <p className="mt-1.5 text-[13px] leading-snug" style={{ color: t.body }}>{m.d}</p>
              </div>
            ))}
          </div>
          <div className="mt-5 border-t pt-3" style={{ borderColor: t.hairline }}>
            <a href="https://beyondthebox.law/caseminder" data-testid="the-why-full-link" className="text-[13.5px] font-semibold leading-snug" style={{ color: t.accent }}>
              There's far more than this — the full set is designed and built inside the founding law firm,
              too long to list here. See it at beyondthebox.law/caseminder →
            </a>
          </div>
        </div>

        {/* Proof + CTA */}
        <div className="mt-7 flex flex-col gap-6 border-t pt-6 md:flex-row md:items-end md:justify-between" style={{ borderColor: t.hairline }}>
          <p className="max-w-[28rem] text-[13px] leading-snug" style={{ color: t.muted }}>
            Built and proven inside a working law firm — <strong style={{ color: t.heading }}>Bonsai Law</strong> runs
            it, and remains the escalation route for regulated legal advice.
          </p>
          <div className="md:text-right">
            <div className="inline-flex items-center gap-2 px-6 py-4 text-sm font-semibold uppercase tracking-[0.12em] text-white" style={{ background: t.cta }}>
              Book a walkthrough <ArrowRight strokeWidth={2} className="h-4 w-4" />
            </div>
            <p className="mt-3 text-[0.7rem] font-semibold uppercase tracking-[0.16em]" style={{ color: t.muted }}>beyondthebox.law</p>
          </div>
        </div>
      </div>

      <style>{`
        @media print {
          .no-print { display: none !important; }
          @page { size: A4; margin: 12mm; }
          [data-testid="the-why-page"] { background: #ffffff !important; padding: 0 !important; }
          #the-why-card { max-width: 100% !important; box-shadow: none !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
      `}</style>
    </div>
  );
}
